import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React__default, { useMemo } from 'react';
import { ResourceDataContextProvider } from '../../../../resource-detail/components/context/ResourceDataContextProvider.js';
import { resourceSharingModal } from '../../../../permission/components/ModalShare/resourceSharingStore.js';
import { getResourceDownloadText } from '../../../utils/getResourceDownloadText.js';
import { IconEye, IconDownload, IconAssistantFace, IconUserPlus } from '@fabric-app/icons/svg';
import { useDownloadResource } from '../../../hooks/useDownloadResource.js';
import { useAskAssistantItem } from '../../../../assistant/hooks/useAskAssistantItem.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { ContextMenu } from '@fabric-app/ui/components/ContextMenu/ContextMenu';
import { useResourceDataContextSafe } from '../../../../resource-detail/components/context/resourceDataContext.js';
import { DropdownItemIconContainer } from '@fabric-app/ui/components/DropdownMenu/Item';
import { ExternalSourceMenuItems } from './ExternalSourceMenuItems.js';
import { ContextMenuCopyResources } from '../../ContextMenu/ContextMenuCopyResources.js';
import { ContextItemMove } from './ContextItemMove.js';
import { ContextItemColorLabels } from './ContextItemLabels.js';
import { ContextItemOpenEnclosingSpace } from './ContextItemOpenEnclosingSpace.js';
import { ContextItemUpdateTags } from './ContextItemUpdateTags.js';
import { ContextItemPinToHome } from './ContextItemPinToHome.js';
import { ContextItemDelete } from './ContextItemDelete.js';
import { useAskAssistantSelection } from '../../../../assistant/hooks/useAskAssistantSelection.js';
import { MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE } from '../../../../assistant/constants.js';
import { useAssistantStore } from '../../../../assistant/stores/assistantStore.js';
import { pick } from '@fabric-app/utils/object/manipulation';
import { ContextItemRename } from './ContextItemRename.js';
import { useContextShareToken } from '../../../../permission/contexts/AnonShareTokenContext.js';

const ResourcePreviewContextMenu = ({ contextDisabled, onItemClickAction, colorLabels, children }) => {
  const { resource, disabledActions } = useResourceDataContextSafe();
  const askAssistantItem = useAskAssistantItem();
  const askAssistantSelection = useAskAssistantSelection();
  const isResourceDownloadable = !!resource.fileUrl;
  const { isMobileView } = useResponsive();
  const handleDownload = useDownloadResource(resource);
  const downloadText = React__default.useMemo(() => getResourceDownloadText(resource), [
    resource
  ]);
  const askAboutItem = () => askAssistantItem?.(resource);
  const addToAssistantContext = () => askAssistantSelection([
    resource.id
  ], true);
  const setShowingShareModal = (show) => {
    resourceSharingModal.open({
      resourceId: resource.id
    }) ;
  };
  const { persistentContext } = useAssistantStore((s) => pick(s, [
    "persistentContext"
  ]));
  const addToAssistantContextDisabledReason = useMemo(() => {
    const newTotal = persistentContext.resourceIds.length + 1;
    return newTotal > MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE ? `You can only add up to ${MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE} items to the assistant context` : void 0;
  }, [
    persistentContext.resourceIds
  ]);
  const shareToken = useContextShareToken();
  return /* @__PURE__ */ jsx(ResourceDataContextProvider, {
    resource,
    parentColorLabels: colorLabels,
    children: /* @__PURE__ */ jsx(ContextMenu, {
      contextDisabled,
      previewAnchor: isMobileView,
      content: /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsxs(ContextMenu.Group, {
            children: [
              onItemClickAction && /* @__PURE__ */ jsxs(ContextMenu.Item, {
                onClick: onItemClickAction,
                "data-testid": "item-context-menu-action-button",
                children: [
                  /* @__PURE__ */ jsx(DropdownItemIconContainer, {
                    children: /* @__PURE__ */ jsx(IconEye, {})
                  }),
                  resource.kind === "folder" ? "Open" : "View"
                ]
              }),
              isResourceDownloadable && /* @__PURE__ */ jsxs(ContextMenu.Item, {
                onClick: handleDownload,
                "data-testid": "item-context-menu-download-button",
                children: [
                  /* @__PURE__ */ jsx(DropdownItemIconContainer, {
                    children: /* @__PURE__ */ jsx(IconDownload, {})
                  }),
                  downloadText
                ]
              }),
              resource.originUrl && /* @__PURE__ */ jsx(ExternalSourceMenuItems, {
                url: resource.originUrl,
                resourceId: resource.id
              }),
              /* @__PURE__ */ jsx(ContextMenuCopyResources, {
                resources: [
                  resource
                ]
              }),
              !shareToken?.isPublishedView && /* @__PURE__ */ jsxs(Fragment, {
                children: [
                  /* @__PURE__ */ jsx(ContextItemMove, {}),
                  /* @__PURE__ */ jsx(ContextItemColorLabels, {
                    colorLabels,
                    rootId: resource.parent?.id
                  }),
                  /* @__PURE__ */ jsx(ContextItemOpenEnclosingSpace, {}),
                  /* @__PURE__ */ jsx(ContextItemUpdateTags, {}),
                  /* @__PURE__ */ jsx(ContextItemRename, {})
                ]
              })
            ]
          }),
          /* @__PURE__ */ jsx(ContextMenu.Group, {
            children: /* @__PURE__ */ jsxs(ContextMenu.Sub, {
              children: [
                /* @__PURE__ */ jsxs(ContextMenu.SubTrigger, {
                  children: [
                    /* @__PURE__ */ jsx(DropdownItemIconContainer, {
                      children: /* @__PURE__ */ jsx(IconAssistantFace, {})
                    }),
                    "Ask AI...",
                    /* @__PURE__ */ jsx(ContextMenu.ChevronRight, {})
                  ]
                }),
                /* @__PURE__ */ jsx(ContextMenu.Portal, {
                  children: /* @__PURE__ */ jsxs(ContextMenu.SubContent, {
                    children: [
                      /* @__PURE__ */ jsx(ContextMenu.Item, {
                        onClick: askAboutItem,
                        children: "Ask about item"
                      }),
                      /* @__PURE__ */ jsx(ContextMenu.Item, {
                        onClick: addToAssistantContext,
                        disabled: !!addToAssistantContextDisabledReason,
                        disabledReason: addToAssistantContextDisabledReason,
                        children: "Add to assistant context"
                      })
                    ]
                  })
                })
              ]
            })
          }),
          !shareToken?.isPublishedView && /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(ContextMenu.Group, {
                children: /* @__PURE__ */ jsx(ContextItemPinToHome, {})
              }),
              /* @__PURE__ */ jsx(ContextMenu.Group, {
                children: /* @__PURE__ */ jsxs(ContextMenu.Item, {
                  onClick: () => setShowingShareModal(),
                  "data-testid": "item-context-menu-share-button",
                  disabledReason: disabledActions.manageSomePermissionRoles,
                  children: [
                    /* @__PURE__ */ jsx(DropdownMenu.ItemIconContainer, {
                      children: /* @__PURE__ */ jsx(IconUserPlus, {})
                    }),
                    resource.isDirectShared ? "Manage sharing" : "Share"
                  ]
                })
              }),
              /* @__PURE__ */ jsx(ContextItemDelete, {})
            ]
          })
        ]
      }),
      children
    })
  });
};

export { ResourcePreviewContextMenu };
//# sourceMappingURL=ResourcePreviewContextMenu.js.map
